import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { GlobalStorage } from 'src/app/apv/core/services/globals.service';
import { BffService } from 'src/app/apv/core/services/http/bff.http.service';
import { IonicStorageService } from 'src/app/apv/core/services/storage/ionic/storage.service';
import {
  dateStringToDateType,
  formatRegime,
  formattedStringDateToddmmyyyySlashs,
  getDateformatMonthYearString,
  stringToRutPDFFormatted,
  toClp,
  truncateTextWithDots,
} from 'src/app/apv/core/utils/formatters';
import {
  dateMonthsDifference,
  downloadPdf,
  getSpanishDateDifferenceFormatted,
  toTitleCase,
} from 'src/app/apv/core/utils/functions';
import {
  CONTRIBUTIONS_CCV_6,
  SUBSCRIPTIONS_TYPES,
} from 'src/app/shared/common/constants';
import { IContributions } from 'src/app/shared/interfaces/contributions.interface';
import { ICurrentSubscription } from 'src/app/shared/interfaces/subscription.interface';
import { ModalService } from '../../../core/services/modal/modal.service';
import { ModifyRegimenComponent } from '../modify-regimen/modify-regimen.component';

const discountType = {
  peso: 'P',
  porcentaje: 'R',
  uf: 'U',
};
@Component({
    selector: 'subscription-card',
    templateUrl: './subscription-card.component.html',
    styleUrls: ['./subscription-card.component.scss'],
    standalone: false
})
export class SubscriptionCardComponent implements OnInit {
  @Input() subscriptionIndex!: number;
  @Input() subscriptionData!: ICurrentSubscription;
  subscriptionDataRaw!: ICurrentSubscription;
  showMessage = false;
  isSubscriptionActive: boolean = true;
  activeLabel: string = '';
  savingSince: string = '';
  savingSinceDate: string = '';
  revokedDate: string = '';
  formattedAmount: string = '';
  lastPaymentDate: string = '';
  CCV_REGIME_MODIFICATION_STATUS = 'apvRegimeModification';
  CCV_AMOUNT_MODIFICATION_STATUS = 'apvAmountModification';
  state = 'success';
  showLastPaymentDateSkeleton: boolean = true;
  user: any = {};
  isMobile: boolean = false;
  isButtonFocused: boolean = false;
  isButtonFocusedRevocar: boolean = false;

  onFocus() {
    this.isButtonFocused = true;
  }

  onFocusRevocar(){
    this.isButtonFocusedRevocar = true;
  }

  onBlur() {
    this.isButtonFocused = false;
  }

  onBlurRevocar(){
    this.isButtonFocusedRevocar = false;
  }

  constructor(
    private readonly modalSvc: ModalService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly bffSvc: BffService,
    private readonly globalSvc: GlobalStorage,
    private readonly ionicStorage: IonicStorageService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.isMobile = (this.document.defaultView as any).Capacitor;
  }

  ngOnInit(): void {
    this.subscriptionDataRaw = { ...this.subscriptionData };
    this.formatTemplateData();
    this.getContributions();
    this.getUserDataFromToken();
  }

  get isEnableAmountModification(): boolean {
    return (
      sessionStorage.getItem(this.CCV_AMOUNT_MODIFICATION_STATUS) === 'true'
    );
  }
  get isEnableRegimeModification(): boolean {
    return (
      sessionStorage.getItem(this.CCV_REGIME_MODIFICATION_STATUS) === 'true'
    );
  }

  private async getContributions(): Promise<void> {
    try {
      const contributionsStorage = (await this.ionicStorage.get(
        CONTRIBUTIONS_CCV_6
      )) as IContributions[];
      this.setContributions(contributionsStorage);
      this.showLastPaymentDateSkeleton = false;
    } catch (error) {
      console.error('Error al cargar cotizaciones:', error);
      this.lastPaymentDate = '--'; //Ver con naty
      this.showLastPaymentDateSkeleton = false;
    }
  }

  private setContributions(contributions: IContributions[]): void {
    if (contributions.length === 0) {
      this.lastPaymentDate = 'No encontrado';
      return;
    }

    this.lastPaymentDate = this.getLastContributionDate(contributions);
  }

  private getLastContributionDate(contributions: IContributions[]): string {
    const employerRutClean = this.subscriptionData.employerRut.replace(
      /^0+/,
      ''
    );

    const thisEmployerLastContribution = contributions.find((contribution) => {
      if (contribution.rutPagador) {
        return contribution.rutPagador.replace(/^0+/, '') === employerRutClean;
      }
      return false;
    });

    if (!thisEmployerLastContribution) {
      return 'No encontrado';
    }

    return thisEmployerLastContribution.fechaCaja;
  }

  private formatTemplateData() {
    this.isSubscriptionActive =
      this.subscriptionData.movementType !== SUBSCRIPTIONS_TYPES.R;

    this.activeLabel = this.isSubscriptionActive ? 'Activa' : 'Inactiva';

    this.subscriptionData.employerName = truncateTextWithDots(
      toTitleCase(this.subscriptionData.employerName),
      24
    );

    this.savingSince = this.isSubscriptionActive
      ? 'Ahorras desde'
      : 'Ahorraste entre';

    this.formattedAmount = this.formatSubscriptionAmount();

    this.savingSinceDate = this.formatSavingSinceDate();
    this.revokedDate = this.formatRevokedDate();
  }

  private formatSavingSinceDate(): string {
    try {
      const [firstPayment] = this.subscriptionData.firstPaymentDate.split('T');
      const slashFormattedBeginDate = firstPayment
        .split('-')
        .reverse()
        .join('/');

      if (this.isSubscriptionActive) {
        const [year, month] = firstPayment.split('-');
        const monthsDifference = dateMonthsDifference(
          new Date(+year, +month),
          new Date()
        );

        if (monthsDifference > 0) {
          return `${slashFormattedBeginDate} (${getSpanishDateDifferenceFormatted(
            monthsDifference
          )})`;
        }

        return slashFormattedBeginDate;
        // Al agregar el modal  volver a: return slashFormattedBeginDate + ' (menos de un mes)';
      }
      //Caso suscripcion inactiva, averiguar con que datos calcular campo Ahorraste entre
      return '--';
    } catch (error) {
      console.error('Sin datos de fecha', error);
      return 'Sin datos';
    }
  }

  formatRevokedDate(): string {
    if (this.isSubscriptionActive || !this.subscriptionData.requestDate) {
      return '';
    }

    const [revokedDate] = this.subscriptionData.requestDate.split('T');
    return revokedDate.split('-').reverse().join('/');
  }

  formatSubscriptionAmount(): string {
    switch (this.subscriptionData.discountType) {
      case discountType.peso:
        return '$' + toClp(this.subscriptionData.discountValue);
      case discountType.uf:
        return 'UF ' + toClp(this.subscriptionData.discountValue);
      case discountType.porcentaje:
        return Math.trunc(this.subscriptionData.discountValue) + '%';
      default:
        return toClp(this.subscriptionData.discountValue);
    }
  }

  labelSubscriptionClass(): string {
    return this.isSubscriptionActive
      ? 'label-active ml-8'
      : 'label-active disabled ml-8';
  }

  openModificationModal(): void {
    sessionStorage.setItem(
      'subscriptionDataToModify',
      JSON.stringify(this.subscriptionDataRaw)
    );

    if (this.isEnableRegimeModification && this.isEnableAmountModification) {
      this.router.navigate(['../option-modify'], {
        relativeTo: this.activatedRoute,
      });
    }
    if (this.isEnableAmountModification && !this.isEnableRegimeModification) {
      this.router.navigate(['../modify'], { relativeTo: this.activatedRoute });
    }
    if (this.isEnableRegimeModification && !this.isEnableAmountModification) {
      this.modalSvc.openCustomModal({
        component: ModifyRegimenComponent,
        cssClass: 'apv-modal',
      });
    }
  }

  openStopSubscriptioModal() {
    sessionStorage.setItem(
      'subscriptionDataToModify',
      JSON.stringify(this.subscriptionDataRaw)
    );

    sessionStorage.setItem(
      'subscriptionDataToStop',
      JSON.stringify(this.subscriptionDataRaw)
    );

    this.router.navigate(['../stop'], {
      relativeTo: this.activatedRoute,
    });
  }
  pickTemplateName(templateName: string) {
    return templateName === 'comprobante_'
      ? 'employerSuscription'
      : 'subscriptionRevocation';
  }
  async descargarComprobanteSuscripcion(templateName: string) {
    try {
      const data = this.buildDataPDF;
      const base64PDF = await this.bffSvc.generatePDF(
        data,
        this.pickTemplateName(templateName)
      );
      downloadPdf(base64PDF.data.data.status, templateName + 'APV.pdf');
      this.state = 'success';
      this.showMessage = true;
    } catch (error) {
      this.state = 'error';
      this.showMessage = true;
      console.log(error);
    }
  }
  closeMessage() {
    this.showMessage = false;
  }

  get buildDataPDF() {
    const rutEmpleador = this.subscriptionData.employerRut.replace(/^0+/, '');
    const rutColaborador = this.user.rut.replace(/^0+/, '');
    const data = {
      ccv: {
        folio: this.subscriptionData.DocumentNumber,
        monto_descuento: this.formattedAmount,
        fecha_suscripcion: formattedStringDateToddmmyyyySlashs(
          this.subscriptionData.requestDate
        ),
        fecha_primer_descuento: getDateformatMonthYearString(
          dateStringToDateType(this.subscriptionData.firstDiscountDate)
        ),
        fecha_inicio_ahorro: getDateformatMonthYearString(
          dateStringToDateType(this.subscriptionData.firstPaymentDate)
        ),
        regimen: formatRegime(this.subscriptionData.taxRegimeCode),
        fondo: `${this.subscriptionData.fundTypeDestination} 100%`,
      },
      colaborador: {
        nombre: toTitleCase(this.user.name),
        rut: stringToRutPDFFormatted(rutColaborador),
        correo: this.user.email,
      },
      empleador: {
        nombre: toTitleCase(this.subscriptionDataRaw.employerName),
        rut: stringToRutPDFFormatted(rutEmpleador),
        correo: this.subscriptionData.employerEmail,
      },
    };
    return data;
  }
  private readonly getUserDataFromToken = () => {
    try {
      const token = this.globalSvc;
      const decodedToken: any = jwt_decode(token.token);

      this.user = {
        name: decodedToken.name,
        lastName: decodedToken.lastName,
        rut: decodedToken.user_id,
        email: decodedToken.email,
      };
    } catch (error) {
      console.error('Error getting user data from token');
      console.log(error);
    }
  };
}
